import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildNewNoteRoute, buildNoteRoute } from '../utils/noteReturnContext';
import DOMPurify from 'dompurify';
import { getWithAuth } from '../utils/apiClient';
import { logger } from "../utils/logger";
import { parseAsUtc } from '../utils/dateUtils';
import { getNoteTypeLabel } from '../features/notes/utils/noteTypeUtils';
import { formatPersonDisambiguator } from '../utils/personDisambiguator';

function GlobalSearch() {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? '⌘' : 'Ctrl';  
  const modSep = isMac ? '' : '+';

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(event) {
      // Cmd+K or Ctrl+K: Open search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      
      // Cmd+N or Ctrl+N: New note (only when not in an input field)
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        const activeElement = document.activeElement;
        const isInputField = activeElement?.tagName === 'INPUT' || 
                            activeElement?.tagName === 'TEXTAREA' || 
                            (activeElement as HTMLElement)?.isContentEditable;
        if (!isInputField) {
          event.preventDefault();
          navigate(buildNewNoteRoute());
        }
      }
      
      // Escape: Close search
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, navigate]);

  const handleSearch = useCallback(async (term) => {
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!term.trim()) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    // Debounce search to avoid excessive API calls
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = [];
        
        // Search patients/clients
        const patientsResponse = await getWithAuth(
          `/patients?search=${encodeURIComponent(term)}`,
          { suppressServerErrorToast: true },
        );
        if (patientsResponse.ok) {
          const patientsData = await patientsResponse.json();
          const patients = patientsData.items || [];
          patients.slice(0, 5).forEach(patient => {
            const firstName = patient.name?.given?.[0] || patient.first_name || '';
            const lastName = patient.name?.family || patient.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
            results.push({
              id: patient.id,
              type: 'client',
              title: fullName,
              // DOB + phone suffix so same-named Person charts are distinguishable pre-open.
              subtitle: [
                formatPersonDisambiguator({ birthdate: patient.birthdate, phone: patient.phone }),
                patient.group || 'Patient',
              ].join(' · '),
              highlight: highlightMatch(fullName, term),
              link: `/clients/${patient.id}`
            });
          });
        }

        // Search treatment cases
        const casesResponse = await getWithAuth(
          `/cases/search?q=${encodeURIComponent(term)}&limit=5`,
          { suppressServerErrorToast: true },
        );
        if (casesResponse.ok) {
          const cases = await casesResponse.json();
          cases.forEach(caseItem => {
            const caseType = caseItem.case_type
              ? `${caseItem.case_type.charAt(0).toUpperCase()}${caseItem.case_type.slice(1)} case`
              : 'Case';
            results.push({
              id: caseItem.id,
              type: 'case',
              title: caseItem.name || 'Unnamed case',
              subtitle: caseType,
              highlight: highlightMatch(caseItem.name || 'Unnamed case', term),
              link: `/cases/${caseItem.id}/overview`,
            });
          });
        }
        
        // Search notes
        const notesResponse = await getWithAuth(
          '/notes/recent',
          { suppressServerErrorToast: true },
        );
        if (notesResponse.ok) {
          const notes = await notesResponse.json();
          notes
            .filter(note => {
              const searchLower = term.toLowerCase();
              const patientName = (note.patient_name || '').toLowerCase();
              const docType = (note.template_name || note.document_type || '').toLowerCase();
              const content = (note.note_content || '').toLowerCase();
              return patientName.includes(searchLower) || 
                     docType.includes(searchLower) || 
                     content.includes(searchLower);
            })
            .slice(0, 5)
            .forEach(note => {
              const dateStr = note.created_at 
                ? parseAsUtc(note.created_at).format('MMM D, YYYY') 
                : 'Unknown date';
              results.push({
                id: note.id,
                type: 'note',
                title: `${note.template_name || getNoteTypeLabel(note.document_type, note.session_type) || 'Note'} - ${note.patient_name || 'Unknown'}`,
                subtitle: `Created ${dateStr}`,
                highlight: highlightMatch(note.patient_name || note.template_name || '', term),
                link: `/notes/${encodeURIComponent(note.id)}`
              });
            });
        }
        
        setSearchResults(results);
      } catch (error) {
        logger.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  // Helper function to highlight matching text (with HTML escaping to prevent XSS)
  const highlightMatch = (text, term) => {
    if (!text || !term) return text;
    // HTML-escape the text first to prevent stored XSS
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return escaped.replace(regex, '<mark>$1</mark>');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value);
  };

  const handleResultClick = (result) => {
    // Build note routes at click time so return_to captures the page the user
    // searched from, not the page they happened to be on when search results
    // were fetched.
    if (result.type === 'note') {
      navigate(buildNoteRoute(result.id));
    } else {
      navigate(result.link);
    }
    setIsOpen(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'note':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        );
      case 'client':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
        );
      case 'case':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7a2 2 0 012-2h5l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
          </svg>
        );
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Mobile: magnifying glass icon only */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 transition duration-150 ease-in-out dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
        aria-label="Search"
      >
        <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>

      {/* Desktop: search bar with placeholder and shortcut badge */}
      <button
        onClick={() => setIsOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400 transition duration-150 ease-in-out cursor-pointer min-w-[180px] lg:min-w-[220px]"
        aria-label="Search"
      >
        <svg className="h-4 w-4 flex-shrink-0" stroke="currentColor" fill="none" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="text-sm truncate">Search...</span>
        <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 font-sans text-[10px] text-gray-400 dark:text-gray-500">
          <span className="text-xs">{modKey}{modSep}</span>K
        </kbd>
      </button>

      {isOpen && (
        <div className="fixed inset-0 overlay-root z-50 overflow-y-auto p-4 sm:p-6 md:p-20" aria-modal="true" role="dialog">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" data-interactive="true" onClick={() => setIsOpen(false)}></div>
          
          <div className="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all dark:bg-gray-800 dark:divide-gray-700" data-interactive="true">
            <div className="relative">
              <svg 
                className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400 dark:text-gray-500" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor" 
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              
              <input
                ref={inputRef}
                type="text"
                className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm dark:text-white"
                placeholder="Search notes, clients..."
                value={searchTerm}
                onChange={handleInputChange}
              />
              
              <div className="absolute right-3 top-3">
                <kbd className="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400 dark:border-gray-700">
                  ESC
                </kbd>
              </div>
            </div>

            {searchTerm && (
              <div className="max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800 dark:text-gray-200">
                {isLoading ? (
                  <div className="px-6 py-4 text-center">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {searchResults.map((result) => (
                      <li 
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className="cursor-pointer px-6 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full ${
                            result.type === 'note' 
                              ? 'text-blue-500 bg-blue-100 dark:bg-blue-900 dark:text-blue-300' 
                              : result.type === 'case'
                                ? 'text-purple-500 bg-purple-100 dark:bg-purple-900 dark:text-purple-300'
                              : 'text-green-500 bg-green-100 dark:bg-green-900 dark:text-green-300'
                          }`}>
                            {getResultIcon(result.type)}
                          </div>
                          
                          <div className="ml-3 flex-1">
                            <div className="font-medium">{result.title}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{result.subtitle}</div>
                            <div 
                              className="mt-1 text-xs text-gray-600 dark:text-gray-300" 
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.highlight) }}
                            ></div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-6 py-6 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No results found</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="py-2 px-6">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Press <kbd className="font-sans">↑</kbd> <kbd className="font-sans">↓</kbd> to navigate
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Press <kbd className="font-sans">Enter</kbd> to select
                </p>
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-semibold">Shortcuts:</span>{' '}
                <kbd className="font-sans">{modKey}</kbd>{modSep}<kbd className="font-sans">K</kbd> search{' · '}
                <kbd className="font-sans">{modKey}</kbd>{modSep}<kbd className="font-sans">N</kbd> new note
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(GlobalSearch);
